/*
 * Lightweight i18n provider for 513travel.com.
 *
 * Keeps the Studio i18n *structure* — a flat dictionary keyed by dotted paths,
 * an active language with persistence, and a t() lookup with a sane fallback —
 * but as a small React context instead of the DOM-attribute (data-i18n) engine
 * the Studio shell used. Only UI chrome is translated.
 *
 * Task 1 ships English copy with the scaffolding for fr/pt/ar/sw ready to fill
 * in. Missing keys fall back to the key's English value, then the key itself.
 */
import { createContext, useContext, useMemo, useState, useCallback } from "react";
import {
  APP_LANGUAGES,
  getAppLanguage,
  setStoredAppLanguage,
  detectVisitorCountry,
} from "./localeConfig.js";

// English dictionary. Other languages can override any subset of these keys;
// anything missing falls back to English.
const EN = {
  "nav.home": "Home",
  "nav.plan": "Plan a Trip",
  "nav.track": "Track Trip",
  "nav.africa": "Africa",
  "nav.flights": "Flights",
  "nav.stays": "Stays",
  "nav.visas": "Visas",
  "nav.experiences": "Experiences",
  "nav.contact": "Contact",
  "nav.studio": "Studio",
  "cta.plan": "Plan a Trip",
  "cta.quote": "Request a Quote",
};

const DICTS = {
  en: EN,
  fr: {},
  pt: {},
  ar: {},
  sw: {},
};

const I18nContext = createContext({
  lang: "en",
  setLang: () => {},
  t: (key, fallback) => fallback ?? key,
  languages: APP_LANGUAGES,
});

export function I18nProvider({ children }) {
  const initial = useMemo(() => {
    try {
      return getAppLanguage(detectVisitorCountry());
    } catch {
      return "en";
    }
  }, []);

  const [lang, setLangState] = useState(initial);

  const setLang = useCallback((next) => {
    if (!APP_LANGUAGES.includes(next)) return;
    setStoredAppLanguage(next);
    setLangState(next);
    if (typeof document !== "undefined") {
      document.documentElement.lang = next;
    }
  }, []);

  const t = useCallback(
    (key, fallback) => {
      const dict = DICTS[lang] || {};
      return dict[key] ?? EN[key] ?? fallback ?? key;
    },
    [lang]
  );

  const value = useMemo(
    () => ({ lang, setLang, t, languages: APP_LANGUAGES }),
    [lang, setLang, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

// Convenience hook returning just the translate function.
export function useT() {
  return useContext(I18nContext).t;
}
